import {
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { OptimNotification } from 'src/app/shared/models/optim-notification';
import { CacheService } from '../../services/cache/cache.service';
import { DataStorageService } from '../../services/dataStorage/data-storage.service';
import {
	EventBusService,
	OptimEventData
} from '../../services/event-bus/event-bus.service';
import { FacadeService } from '../../services/facade.service';

@Component({
	selector: 'app-notification-manager',
	templateUrl: './notification-manager.component.html',
	styleUrls: ['./notification-manager.component.scss']
})
export class NotificationManagerComponent implements OnChanges {
	/**
	 * Nombre de notifications (nombre affiché dans le badge-pill)
	 */
	notificationNumber = 0;

	@Input() optimNotifications: OptimNotification[] = [];

	notificationList: {
		title: string;
		message: string;
		optimState: string;
		weekNumber: number;
		year: number;
	}[] = [];

	constructor(
		public router: Router,
		private facade: FacadeService,
		private cacheService: CacheService,
		private eventBusService: EventBusService,
		private dataStorageService: DataStorageService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if(!changes.optimNotifications.isFirstChange()) {
			this.activateGetNotifications();
		}
	}

	activateGetNotifications() {
		let optimQueueOfSessionStorage =
			this.facade.dataStorageService.getOptimQueue();

		const optimQueueFromServer = this.optimNotifications;

		// si sessionStorage vide => sessionStorage = données du serveur
		if (
			!optimQueueOfSessionStorage ||
			optimQueueOfSessionStorage.length === 0
		) {
			optimQueueOfSessionStorage = optimQueueFromServer;
			this.facade.dataStorageService.setOptimQueue(optimQueueFromServer);
		}

		if (
			optimQueueFromServer?.length > 0 ||
			optimQueueOfSessionStorage?.length > 0
		) {
			// on met à jour le sessionStorage par rapport aux données du serveur
			const hasStorageBeenUpdated = this.updateQueueOfSessionStorage(
				optimQueueOfSessionStorage,
				optimQueueFromServer
			);
			if (hasStorageBeenUpdated) {
				optimQueueOfSessionStorage =
					this.facade.dataStorageService.getOptimQueue();
			}

			optimQueueOfSessionStorage.forEach(
				(optimNotifFromSessionStorage: OptimNotification) => {
					// cas où notif du serveur absente et notif localStorage présente => l'optim est terminée
					if (
						this.hasAnOptimisationFinished(
							optimNotifFromSessionStorage,
							optimQueueFromServer
						)
					) {
						this.processOptimisationIsFinished(optimNotifFromSessionStorage);
					} else {
						// Planification en cours
						// On prévient le planning qu'une optim est en cours (activation du app-loading component)
						this.processOptimisationInProgress(optimNotifFromSessionStorage);
					}
				}
			);
		}
	}

	hasAnOptimisationFinished(
		optimNotif: OptimNotification,
		optimQueueFromServer: OptimNotification[]
	): boolean {
		return (
			!optimQueueFromServer ||
			optimQueueFromServer.findIndex(
				(optimQueue) =>
					optimQueue.year === optimNotif.year &&
					optimQueue.weekNumber === optimNotif.weekNumber
			) === -1 ||
			optimQueueFromServer.length === 0
		);
	}

	processOptimisationIsFinished(optimNotif: OptimNotification) {
		this.cacheService.clearCache();

		// on vérifie si on a pas déjà une notification affichée côté front pour la semaine et l'année donnée
		const notifAlreadyExisting = this.notificationList.find((notif) => {
			return (
				notif.weekNumber === optimNotif.weekNumber &&
				notif.year === optimNotif.year
			);
		});

		// si elle existe et n'est pas finished, on supprime l'existante
		if (
			notifAlreadyExisting &&
			notifAlreadyExisting.optimState !== 'finished'
		) {
			this.notificationList =
				this.removeNotificationByYearAndWeekNumber(optimNotif);
		}

		// si une notification avec "finished" n'existe pas déjà, on la crée
		if (
			!notifAlreadyExisting ||
			(notifAlreadyExisting && notifAlreadyExisting.optimState !== 'finished')
		) {
			const messageToast = `Planification semaine ${optimNotif.weekNumber} terminée`;
			this.notificationList = [
				...this.notificationList,
				{
					title: messageToast,
					message: 'Cliquez ici pour y accéder !',
					optimState: 'finished',
					weekNumber: optimNotif.weekNumber,
					year: optimNotif.year
				}
			];

			this.notificationNumber++;
		}

		// Update du session storage
		let optimQueueOfSessionStorage =
			this.facade.dataStorageService.getOptimQueue();
		optimQueueOfSessionStorage = optimQueueOfSessionStorage.filter(
			(notif) => notif.weekNumber !== optimNotif.weekNumber
		);
		this.facade.dataStorageService.setOptimQueue(optimQueueOfSessionStorage);

		// On prévient le planning que c'est terminé (désactivation du app-loading component)
		this.eventBusService.emit(
			new OptimEventData(
				'getIsOptimInProgressFromNavbar',
				false,
				optimNotif.weekNumber
			)
		);
	}

	processOptimisationInProgress(optimNotif: OptimNotification) {
		this.eventBusService.emit(
			new OptimEventData(
				'getIsOptimInProgressFromNavbar',
				true,
				optimNotif.weekNumber
			)
		);

		// on vérifie si on a pas déjà une notification affichée côté front pour la semaine et l'année donnée
		const notifAlreadyExisting = this.notificationList.find((notif) => {
			return (
				notif.weekNumber === optimNotif.weekNumber &&
				notif.year === optimNotif.year
			);
		});

		// si ce n'est pas le cas et qu'elle est inProgress, ou qu'on en a une mais qu'elle n'est pas inProgress
		if (
			(!notifAlreadyExisting && optimNotif.inProgress === true) ||
			(notifAlreadyExisting &&
				notifAlreadyExisting.optimState !== 'inProgress' &&
				optimNotif.inProgress === true)
		) {
			// si ce n'est pas le cas, on retire celle côté front (celle affichée)
			this.notificationList =
				this.removeNotificationByYearAndWeekNumber(optimNotif);

			// et on fait afficher la nouvelle notification avec le bon statut
			this.notificationList.push({
				title: 'Planification en cours',
				message: 'Semaine ' + optimNotif.weekNumber,
				optimState: 'inProgress',
				weekNumber: optimNotif.weekNumber,
				year: optimNotif.year
			});
		}
		// si ce n'est pas le cas et qu'elle est waiting, ou qu'on en a une mais qu'elle n'est pas waiting
		else if (
			(!notifAlreadyExisting &&
				notifAlreadyExisting.optimState === 'waiting') ||
			(notifAlreadyExisting &&
				notifAlreadyExisting.optimState !== 'waiting' &&
				optimNotif.waiting === true)
		) {
			// si ce n'est pas le cas, on retire celle côté front (celle affichée)
			this.notificationList =
				this.removeNotificationByYearAndWeekNumber(optimNotif);

			// et on fait afficher la nouvelle notification avec le bon statut
			this.notificationList.push({
				title: 'Planification en attente',
				message:
					'Semaine ' +
					optimNotif.weekNumber +
					`est en ${
						optimNotif.positionInWaitingQueue + 1 === 1
							? optimNotif.positionInWaitingQueue + 1 + 'ère'
							: optimNotif.positionInWaitingQueue + 1 + 'ème'
					} position dans la file d'attente.`,
				optimState: 'waiting',
				weekNumber: optimNotif.weekNumber,
				year: optimNotif.year
			});
		}
	}

	updateQueueOfSessionStorage(
		qSessionStorage: OptimNotification[],
		qServer: OptimNotification[]
	): boolean {
		let isSaveInStorageNeeded = false;

		// on boucle sur toutes les optims de la file d'attente du serveur
		qServer.forEach((optimServer) => {
			let optimNotifOfStorage = qSessionStorage.find(
				(optimStorage) =>
					optimStorage.weekNumber === optimServer.weekNumber &&
					optimStorage.year === optimServer.year
			);
			// si l'optimServer n'est pas présente dans l'optimLocale => on l'ajoute
			if (!optimNotifOfStorage) {
				qSessionStorage.push(optimServer);
				isSaveInStorageNeeded = true;
			} else {
				// Sinon si elle est présente, on regarde si elle a été modifiée, auquel cas on met à jour
				const updateNeeded = this.compareOptims(
					optimNotifOfStorage,
					optimServer
				);
				if (updateNeeded) {
					isSaveInStorageNeeded = true;
				}
			}
		});

		// si une modif a eu lieu, on met à jour le sessionStorage avec la nouvelle Queue
		if (isSaveInStorageNeeded) {
			this.facade.dataStorageService.setOptimQueue(qSessionStorage);
		}

		return isSaveInStorageNeeded;
	}

	compareOptims(
		optimStorage: OptimNotification,
		optimServer: OptimNotification
	): boolean {
		if (optimServer.inProgress !== optimStorage.inProgress) {
			return true;
		} else if (
			optimServer.positionInWaitingQueue !== optimStorage.positionInWaitingQueue
		) {
			return true;
		} else if (optimServer.waiting !== optimStorage.waiting) {
			return true;
		} else {
			return false;
		}
	}

	getNotificationDisplayClass(notification: {
		title: string;
		message: string;
		optimState: string;
		weekNumber: number;
		year: number;
	}) {
		return {
			'alert ': notification.optimState !== null,
			'hand-cursor ': notification.optimState !== null,
			'unselectable-text ': notification.optimState !== null,
			'alert-success': notification.optimState === 'finished',
			'alert-secondary': notification.optimState === 'inProgress',
			'alert-warning': notification.optimState === 'waiting'
		};
	}

	goToPlanningPage(notification: {
		title: string;
		message: string;
		optimState: string;
		weekNumber: number;
		year: number;
	}) {
		this.router.routeReuseStrategy.shouldReuseRoute = function () {
			return false;
		};
		this.router.onSameUrlNavigation = 'reload';

		// Retirer la notification sur laquelle on a cliqué
		this.notificationList = this.notificationList.filter(
			(notif) => notif.weekNumber !== notification.weekNumber
		);

		this.dataStorageService.setParam(
			'storedCalendarWeeknumber',
			notification.weekNumber
		);
		this.dataStorageService.setParam(
			'storedCalendarYearnumber',
			notification.year
		);

		// TODO : récupérer param de centre pour solution unique ou multiple
		if (this.dataStorageService.isOptimMultipleActive()) {
			this.dataStorageService.setParam('openReport', true);
		}
		this.router.navigate(['/planning']);
	}

	removeNotificationByYearAndWeekNumber(optimNotif: OptimNotification) {
		return this.notificationList.filter((notif) => {
			return notif.weekNumber !== optimNotif.weekNumber;
		});
	}

	resetNotificationNumber() {
		this.notificationNumber = 0;
	}
}
